import blogService from '@/services/blogService';

const state = {
    posts: [],
    currentPost: null,
    relatedPosts: [],
    categories: [],
    tags: [],
    loading: false,
    error: null,
    pagination: {
        page: 1,
        perPage: 9,
        total: 0,
    },
    filters: {
        category: null,
        tag: null,
        search: null,
        featured: false,
    },
    searchResults: [],
};

const mutations = {
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_POSTS(state, posts) {
        state.posts = posts;
    },
    SET_CURRENT_POST(state, post) {
        state.currentPost = post;
    },
    SET_CATEGORIES(state, categories) {
        state.categories = categories;
    },
    SET_TAGS(state, tags) {
        state.tags = tags;
    },
    SET_PAGINATION(state, pagination) {
        state.pagination = { ...state.pagination, ...pagination };
    },
    SET_FILTERS(state, filters) {
        state.filters = { ...state.filters, ...filters };
    },
    SET_SEARCH_RESULTS(state, results) {
        state.searchResults = results;
    },
    CLEAR_SEARCH_RESULTS(state) {
        state.searchResults = [];
    },
    SET_RELATED_POSTS(state, relatedPosts) {
        state.relatedPosts = relatedPosts;
    },
    CLEAR_RELATED_POSTS(state) {
        state.relatedPosts = [];
    },
};

const actions = {
    /**
     * Fetch all blog posts with optional filtering
     */
    async fetchPosts({ commit, state }, filters = {}) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const combinedFilters = { ...state.filters, ...filters };
            const response = await blogService.getAllPosts(combinedFilters);

            commit('SET_POSTS', response.posts);
            commit('SET_PAGINATION', {
                page: response.pagination.currentPage,
                perPage: response.pagination.perPage,
                total: response.pagination.total,
            });
            commit('SET_FILTERS', combinedFilters);
        } catch (error) {
            commit('SET_ERROR', error.message);
            console.error('Error fetching posts:', error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Fetch a single blog post by slug
     */
    async fetchPostBySlug({ commit }, slug) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const post = await blogService.getPost(slug);
            commit('SET_CURRENT_POST', post);
        } catch (error) {
            commit('SET_ERROR', error.message);
            console.error('Error fetching post:', error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Fetch all categories
     */
    async fetchCategories({ commit }) {
        try {
            const categories = await blogService.getCategories();
            commit('SET_CATEGORIES', categories);
        } catch (error) {
            commit('SET_ERROR', error.message);
            console.error('Error fetching categories:', error);
        }
    },

    /**
     * Fetch all tags
     */
    async fetchTags({ commit }) {
        try {
            const tags = await blogService.getTags();
            commit('SET_TAGS', tags);
        } catch (error) {
            commit('SET_ERROR', error.message);
            console.error('Error fetching tags:', error);
        }
    },

    /**
     * Search blog posts
     */
    async searchPosts({ commit }, query) {
        if (!query || query.trim() === '') {
            commit('CLEAR_SEARCH_RESULTS');
            return;
        }

        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const results = await blogService.searchPosts(query);
            commit('SET_SEARCH_RESULTS', results);
        } catch (error) {
            commit('SET_ERROR', error.message);
            console.error('Error searching posts:', error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Get related posts for current post
     */
    async fetchRelatedPosts({ commit, state }, limit = 3) {
        if (!state.currentPost) {
            commit('CLEAR_RELATED_POSTS');
            return;
        }

        try {
            const relatedPosts = await blogService.getRelatedPosts(state.currentPost, limit);
            commit('SET_RELATED_POSTS', relatedPosts);
            return relatedPosts;
        } catch (error) {
            commit('SET_ERROR', error.message);
            console.error('Error fetching related posts:', error);
            commit('CLEAR_RELATED_POSTS');
            return [];
        }
    },

    /**
     * Initialize blog data
     */
    async initializeBlog({ dispatch }) {
        await Promise.all([
            dispatch('fetchPosts'),
            dispatch('fetchCategories'),
            dispatch('fetchTags'),
        ]);
    },

    /**
     * Clear current post
     */
    clearCurrentPost({ commit }) {
        commit('SET_CURRENT_POST', null);
        commit('CLEAR_RELATED_POSTS');
    },

    /**
     * Clear search results
     */
    clearSearch({ commit }) {
        commit('CLEAR_SEARCH_RESULTS');
    },
};

const getters = {
    /**
     * Get posts with loading state
     */
    postsWithLoading: (state) => ({
        posts: state.posts,
        loading: state.loading,
        error: state.error,
    }),

    /**
     * Get current post with loading state
     */
    currentPostWithLoading: (state) => ({
        post: state.currentPost,
        loading: state.loading,
        error: state.error,
    }),

    /**
     * Get featured posts
     */
    featuredPosts: (state) => {
        return state.posts.filter((post) => post.featured);
    },

    /**
     * Get posts by category
     */
    postsByCategory: (state) => (category) => {
        return state.posts.filter((post) => post.category.toLowerCase() === category.toLowerCase());
    },

    /**
     * Get posts by tag
     */
    postsByTag: (state) => (tag) => {
        return state.posts.filter((post) =>
            post.tags.some((t) => t.toLowerCase() === tag.toLowerCase()),
        );
    },

    /**
     * Get pagination info
     */
    paginationInfo: (state) => {
        const { page, perPage, total } = state.pagination;
        const totalPages = Math.ceil(total / perPage);

        return {
            currentPage: page,
            totalPages,
            total,
            perPage,
            hasNextPage: page < totalPages,
            hasPrevPage: page > 1,
        };
    },

    /**
     * Get active filters
     */
    activeFilters: (state) => {
        return Object.entries(state.filters)
            .filter(([_, value]) => value !== null && value !== false)
            .reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {});
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
